import { label } from './snippet.js';
import { chatIconSvg } from './leadwin-agent.js';
import { launcherColors, panelChrome } from './color.js';
import { isMobileViewport } from './page.js';

/**
 * The panel's two sizes. The corner one is what a chat needs; the reading one is what an article does — a
 * help-center page set in a 410px column is a word or five per line. Both stay under the max-width and
 * max-height the panel carries, so the reading size costs nothing on a small screen: it is clamped back to
 * roughly the corner size and the visitor simply reads in the box they had.
 * Exported so the dashboard's preview drawer can size its own frame by the same numbers — it hosts the
 * widget without this loader, and two sets of numbers would drift.
 */
export const PANEL_WIDTH = '410px';
export const PANEL_HEIGHT = '720px';
export const PANEL_MAX_HEIGHT = 'calc(100vh - 120px)';

/**
 * Reading size: only half again as wide, but as tall as the viewport allows. An article is a column of
 * prose — it gains far more from length than from width, and past roughly 70 characters a line gets harder
 * to read, not easier. The ceiling rises with it, down to the room the launcher itself needs.
 */
export const PANEL_WIDTH_EXPANDED = '640px';
export const PANEL_HEIGHT_EXPANDED = '1200px';
export const PANEL_MAX_HEIGHT_EXPANDED = 'calc(100vh - 96px)';

/**
 * How the box travels between its two sizes. One curve for both directions, decelerating hard: it leaves
 * the old size at speed and settles into the new one over a long tail, which is what makes a resize read
 * as the panel arriving somewhere rather than as a box being redrawn. A near-even curve over a short
 * distance is what looks mechanical - the eye only ever gets to see the constant middle of it.
 *
 * The article panel inside the frame slides in on the same curve over the same expand duration
 * (WidgetIntercomArticlePanel.razor), so the box and its content settle together instead of in two steps.
 */
export const PANEL_SIZE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Growing takes the longer time: the panel is claiming room on somebody else's page, and the visitor needs
 * that beat to follow where the edges went. Shrinking is a retreat - the visitor already knows where the
 * corner is, and dwelling on the way back only feels slow.
 */
export const PANEL_EXPAND_MS = 480;
export const PANEL_COLLAPSE_MS = 360;

/**
 * How far the corner panel and the launcher stand off the window edges. Was written into three cssText
 * strings and four applySide calls as a bare '18px'; named here because the fullscreen branch below has
 * to put the very same values BACK when a rotation returns the visitor to a roomy screen.
 */
export const PANEL_EDGE_OFFSET = '18px';

/** Clear of the 48px launcher and its own 18px offset, with room for the halo it pulses. */
export const PANEL_BOTTOM_OFFSET = '84px';

/* The round-trip probe's answer, asked once and remembered — see supportsSafeArea. */
let safeAreaSupport = null;

/**
 * Whether this engine really keeps an env(safe-area-inset-*) the loader writes through the CSSOM.
 *
 * Two shapes suggest themselves and neither holds. Writing the plain offset and then the calc over the top
 * of it — the fallback pattern of a stylesheet — assumes an engine that cannot parse the second one drops
 * it; an engine is equally free to ACCEPT the declaration and keep a mangled value, and the good offset
 * underneath is then lost to a string that means nothing. Asking CSS.supports assumes the answer is about
 * this engine's parser, and at least one (the test environment's) says yes and mangles anyway.
 *
 * So the probe writes the value onto a detached element and reads it back: an engine that kept the
 * function it was handed serializes it with the function intact. Anything else — dropped, rewritten,
 * reordered — fails the check and leaves the widget on the plain pixel offsets it has always used, which
 * is no loss: an engine this old is not running on a phone with a notch.
 * @returns {boolean}
 */
export function supportsSafeArea() {
    if (safeAreaSupport === null) {
        try {
            const probe = document.createElement('div');
            probe.style.bottom = 'calc(1px + env(safe-area-inset-bottom,0px))';
            safeAreaSupport = probe.style.bottom.indexOf('env(safe-area-inset-bottom') >= 0;
        } catch {
            safeAreaSupport = false;
        }
    }

    return safeAreaSupport;
}

/**
 * An offset from one window edge, held clear of whatever the device keeps there — the notch in landscape,
 * the home indicator at the bottom. Everything the loader pins to the corner goes through it, so the
 * launcher, the bubbles above it and the trust cards above those all rise by the same amount instead of
 * landing on top of one another on the one device that has an inset.
 * @param {string} offset - The plain offset, e.g. '18px'
 * @param {string} edge - 'top' | 'right' | 'bottom' | 'left'
 * @returns {string} A CSS length
 */
export function withSafeArea(offset, edge) {
    return supportsSafeArea()
        ? 'calc(' + offset + ' + env(safe-area-inset-' + edge + ',0px))'
        : offset;
}

/**
 * Whether the panel takes the whole screen instead of a corner of it. Below 640px — the app's own narrow
 * breakpoint, the one the bubbles already measure themselves against — a 410px box floating over a 390px
 * phone is not a corner panel at all: it covers the page anyway, but does it with a hairline of somebody
 * else's website showing around three of its edges and a launcher sitting on top of its own chat. A chat
 * that takes the screen is what every messenger on a phone does, and it is the only size on which the
 * composer, the tab bar and the panels inside have room to be tapped.
 * @returns {boolean}
 */
export function isPanelFullscreen() {
    return isMobileViewport();
}

/**
 * Writes the panel's whole box for the room it is standing in — the corner rectangle at its two sizes, or
 * the phone's full screen. One function rather than a style string per caller: the open, the article's
 * resize, a live position change and the crossing of the breakpoint itself all have to agree on every
 * property, and the fullscreen branch only works if it can UNSET what the corner branch wrote (and the
 * other way round). Everything either branch touches, the other one writes back.
 *
 * The safe area is padding on the panel, not on the page inside it: `env(safe-area-inset-*)` resolves to
 * zero inside a cross-origin iframe — the frame knows nothing about the notch of the device its embedder
 * is drawn on — so the widget's own header would sit under the status bar and its tab bar under the home
 * indicator. Holding the frame off here keeps both clear, and the strips it leaves are painted by the
 * panel's own ground rather than by the host page.
 * @param {HTMLDivElement} panel - The panel from createPanel
 * @param {{side: string}} options - Normalized rendering options
 * @param {boolean} expanded - True for the reading size (ignored on a fullscreen panel: it is already max)
 */
export function applyPanelGeometry(panel, options, expanded) {
    const edge = panel.querySelector('#leadwin-agent-edge');

    if (isPanelFullscreen()) {
        panel.style.top = '0';
        panel.style.right = '0';
        panel.style.bottom = '0';
        panel.style.left = '0';
        panel.style.width = 'auto';
        panel.style.height = 'auto';
        panel.style.maxWidth = 'none';
        panel.style.maxHeight = 'none';
        panel.style.borderRadius = '0';
        panel.style.boxShadow = 'none';
        panel.style.padding = supportsSafeArea()
            ? 'env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) '
                + 'env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)'
            : '0';

        if (edge) {
            // Nothing to outline: the box has no corner to separate from the page — it IS the page. The
            // 1.25px stroke would only draw a frame around the phone's screen.
            edge.style.borderRadius = '0';
            edge.style.display = 'none';
        }

        return;
    }

    panel.style.top = 'auto';
    applySide(panel, options.side, PANEL_EDGE_OFFSET);
    panel.style.bottom = PANEL_BOTTOM_OFFSET;
    panel.style.width = expanded === true ? PANEL_WIDTH_EXPANDED : PANEL_WIDTH;
    panel.style.height = expanded === true ? PANEL_HEIGHT_EXPANDED : PANEL_HEIGHT;
    panel.style.maxWidth = 'calc(100vw - 32px)';
    panel.style.maxHeight = expanded === true ? PANEL_MAX_HEIGHT_EXPANDED : PANEL_MAX_HEIGHT;
    panel.style.borderRadius = '24px';
    // The look's own shadow, parked on the panel by paintPanelGround: the corner branch has to put back
    // exactly what the fullscreen branch dropped, and only the colour resolution knows what that was.
    panel.style.boxShadow = panel.style.getPropertyValue('--lw-panel-shadow');
    panel.style.padding = '0';

    if (edge) {
        edge.style.borderRadius = '24px';
        edge.style.display = '';
    }
}

/**
 * The transition for one direction. Width, height and the ceiling move as one - the ceiling is what
 * actually governs the height on most screens, so leaving it on another timing would visibly tear the box.
 * @param {number} durationMs - PANEL_EXPAND_MS or PANEL_COLLAPSE_MS
 * @returns {string} A CSS transition value
 */
export function panelSizeTransition(durationMs) {
    const step = ' ' + durationMs + 'ms ' + PANEL_SIZE_EASING;
    return 'width' + step + ', height' + step + ', max-height' + step;
}

/**
 * Moves a fixed element to the given screen side (live position change from the manage panel).
 * @param {HTMLElement} el - Launcher button or panel
 * @param {string} side - 'left' | 'right'
 * @param {string} offset - CSS offset from the chosen edge
 */
export function applySide(el, side, offset) {
    // Held clear of the phone's rounded corner where there is one — in landscape the two insets differ,
    // so the side the element stands on decides which of them counts.
    const held = withSafeArea(offset, side === 'left' ? 'left' : 'right');

    el.style.left = side === 'left' ? held : '';
    el.style.right = side === 'right' ? held : '';
}

/**
 * Creates the fixed-position panel that hosts the chat iframe (hidden until first open).
 * @param {{side: string, theme: string}} options - Normalized rendering options
 * @returns {HTMLDivElement}
 */
export function createPanel(options) {
    const panel = document.createElement('div');
    const chrome = panelChrome(options.theme, options.tint);
    panel.id = 'leadwin-agent-panel';
    // Only what holds for every size the panel has. Where its edges are, how round they are and what it
    // does with the phone's safe area is applyPanelGeometry's, called once here and again on every open,
    // resize and rotation — border-box because the fullscreen branch pads the frame off the notch.
    panel.style.cssText =
        'position:fixed;z-index:2147483646;box-sizing:border-box;overflow:hidden;display:none;';

    /* The loading dots, built before the iframe so the frame paints OVER them once it has something to
     * show. A panel that opens onto a blank frame reads as broken for the whole first load; three
     * pulsing dots read as the chat coming. Styling lives in the injected sparkle styles. */
    const loading = document.createElement('div');
    loading.id = 'leadwin-agent-loading';
    loading.style.color = '#9ca3af';
    for (let i = 0; i < 3; i++) {
        loading.appendChild(document.createElement('span'));
    }
    panel.appendChild(loading);

    /* The outline, drawn OVER the iframe rather than as a border around it. The iframe composites as
     * its own layer, and the rounded clip this panel puts on that layer gets no anti-aliasing — a
     * bright border hugging that staircase is what made the corners look pixelated on dark pages. The
     * overlay's inset stroke is anti-aliased like any painted curve and covers the clipped edge, and
     * being inset it eats into the 410 x 720 the panel opens with rather than growing it. */
    const edge = document.createElement('div');
    edge.id = 'leadwin-agent-edge';
    edge.style.cssText = 'position:absolute;inset:0;border-radius:24px;pointer-events:none;';
    panel.appendChild(edge);
    paintPanelGround(panel, chrome);
    applyPanelGeometry(panel, options, false);

    document.body.appendChild(panel);
    return panel;
}

/**
 * Paints the panel's ground, shadow and outline for one look. The shadow is parked on the element as a
 * custom property as well as applied: a fullscreen panel drops it (a box with no corners casts nothing),
 * and when a rotation hands the visitor a roomy screen back, applyPanelGeometry needs the value again —
 * without a place to keep it the corner panel would come back flat, or this file would have to resolve
 * the colours a second time and could drift from the first.
 * @param {HTMLDivElement} panel - The panel from createPanel
 * @param {{background: string, border: string, shadow: string}} chrome - What panelChrome resolved
 */
export function paintPanelGround(panel, chrome) {
    panel.style.background = chrome.background;
    panel.style.setProperty('--lw-panel-shadow', chrome.shadow);
    panel.style.boxShadow = isPanelFullscreen() ? 'none' : chrome.shadow;
    paintPanelEdge(panel, chrome.border);
}

/**
 * Paints the panel's outline for the given look — on the edge overlay, not the panel itself (see
 * createPanel for why). Shared by the first paint and every later theme or branding switch, so the two
 * can never drift apart.
 * 1.25px rather than a hairline — at 1px the edge dropped out again on the first dark page.
 * @param {HTMLDivElement} panel - The panel from createPanel
 * @param {string} color - Outline color resolved by panelChrome; 'transparent' on a light page
 */
export function paintPanelEdge(panel, color) {
    const edge = panel.querySelector('#leadwin-agent-edge');
    if (edge) {
        edge.style.boxShadow = 'inset 0 0 0 1.25px ' + color;
    }
}

/**
 * Paints the launcher's closed mark: the animated sparkles, or the provider's own picture. The picture is
 * built as an element rather than markup — it is the one thing in this button that comes from a stored
 * value, and an <img> assembled by hand can carry no attribute the payload did not put in the src.
 *
 * Contained rather than cropped: a launcher picture is as often a wide wordmark as a square avatar, and
 * cover would cut the first one in half. The accent circle stays visible around it, which is also what
 * keeps a transparent PNG readable.
 * @param {HTMLButtonElement} button - The launcher button
 * @param {{launcherIcon: string, launcherIconUrl: string|null}} options - Normalized rendering options
 */
export function paintLauncherIcon(button, options) {
    if (options.launcherIcon === 'custom' && options.launcherIconUrl) {
        const image = document.createElement('img');
        image.alt = '';
        image.src = options.launcherIconUrl;
        image.style.cssText =
            'width:32px;height:32px;object-fit:contain;border-radius:6px;pointer-events:none';
        button.replaceChildren(image);
        return;
    }

    button.innerHTML = chatIconSvg;
}

/**
 * Points the halo at the accent of the look the launcher is standing in, and tells it which look that is
 * — the ring is hollow and carries no fill to inherit, and on a dark ground it needs more of itself to
 * register (see .lw-launcher-pulse in leadwin-nudge.css). Shared by the first paint and every later
 * theme switch, so the two can never drift apart.
 * @param {HTMLElement} button - The launcher button
 * @param {{pulse: string, dark: boolean}} colors - What launcherColors resolved for the current look
 */
export function paintLauncherPulse(button, colors) {
    button.style.setProperty('--lw-launcher-pulse', colors.pulse);
    button.classList.toggle('lw-launcher-pulse-dark', colors.dark);
}

/**
 * Creates the floating launcher button (sparkles icon by default) in the configured accent fill — a flat
 * colour, or the gradient the contractor switched on (see launcherColors).
 * @param {{accent: string, contrast: string, accentDark: string, contrastDark: string, side: string,
 *          theme: string, launcherIcon: string, launcherIconUrl: string|null}} options - Normalized
 *          rendering options
 * @returns {HTMLButtonElement}
 */
export function createButton(options) {
    const colors = launcherColors(options, options.theme);
    const button = document.createElement('button');
    button.id = 'leadwin-agent-button';
    /* The pulsing accent halo, defined once in leadwin-nudge.css so the manage panel's preview
     * launcher pulses with the very same numbers. Pauses via [aria-expanded="true"] while open. */
    button.className = 'lw-launcher-pulse';
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-expanded', 'false');
    /* padding:0 and border-box are not cosmetics here: the halo is positioned against this button's
     * padding box, and a host page (or the browser's own button defaults) putting padding on it would
     * shift the ring off the circle it belongs to. Pinning both keeps the 48px circle exact everywhere. */
    /* The bottom offset is held clear of the home indicator on a phone without a home button: that bar
     * sits exactly where an 18px launcher does, and a swipe aimed at the sparkles opened the app switcher
     * instead. The side offset is applySide's, which holds the other edge the same way. */
    button.style.cssText =
        'position:fixed;bottom:' + withSafeArea(PANEL_EDGE_OFFSET, 'bottom') + ';z-index:2147483647;' +
        'box-sizing:border-box;width:48px;height:48px;padding:0;' +
        'border-radius:9999px;border:none;cursor:pointer;' +
        'background-color:' + colors.background + ';color:' + colors.color + ';' +
        'display:flex;align-items:center;justify-content:center;';
    applySide(button, options.side, PANEL_EDGE_OFFSET);
    // Set apart from the cssText above: a gradient belongs on top of the flat accent, not instead of it.
    button.style.backgroundImage = colors.gradient;
    paintLauncherPulse(button, colors);
    paintLauncherIcon(button, options);
    document.body.appendChild(button);
    return button;
}
